import React, { useEffect, useRef, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import './social-proof.scss';

interface Props {
  children: ReactNode;
}

const socialLogos = [
  {
    alt: 'Amazon logo',
    width: '248',
    height: '75',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo1.png'
  },
  {
    alt: 'Adobe logo',
    width: '123',
    height: '141',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo2.png'
  },
  {
    alt: 'Microsoft logo',
    width: '348',
    height: '76',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/logos/microsoft-@2x.png'
  },
  {
    alt: 'Cisco logo',
    width: '194',
    height: '103',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo4.png'
  },
  {
    alt: 'Google logo',
    width: '236',
    height: '78',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo15.png'
  },
  {
    alt: 'Whole Foods logo',
    width: '200',
    height: '140',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/logos/wholefoods-@2x.png'
  },
  {
    alt: 'Wells Fargo logo',
    width: '115',
    height: '116',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo6.png'
  },
  {
    alt: 'Marketo logo',
    width: '243',
    height: '106',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo7.png'
  },
  {
    alt: 'Netflix logo',
    width: '252',
    height: '70',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo9.png'
  },
  {
    alt: 'Log Me In logo',
    width: '267',
    height: '78',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo10.png'
  },
  {
    alt: 'Okta logo',
    width: '204',
    height: '70',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo11.png'
  },
  {
    alt: 'Intel logo',
    width: '189',
    height: '125',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo12.png'
  },
  {
    alt: 'HP logo',
    width: '130',
    height: '130',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo13.png'
  },
  {
    alt: 'GE logo',
    width: '137',
    height: '137',
    src:
      'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/logos/logo16.png'
  }
];

const SocialProof = ({ children }: Props) => {
  const [ref, inView] = useInView();
  const hasBeenInView = useRef(false);

  useEffect(() => {
    if (inView && !hasBeenInView.current) {
      hasBeenInView.current = true;

      const logos = Array.from(
        document.querySelectorAll<HTMLImageElement>('.social-proof-slider img')
      );

      const padding = 120;
      const pixelsPerSecond = 30;
      let containerWidth = 0;

      logos.forEach(logo => {
        const itemWidth = logo.hasAttribute('width')
          ? Number(logo.getAttribute('width')) / 2.0
          : 0;
        const itemHeight = logo.hasAttribute('height')
          ? Number(logo.getAttribute('height'))
          : 0;

        containerWidth += itemWidth + padding;

        const delay = containerWidth / pixelsPerSecond;

        // eslint-disable-next-line no-param-reassign
        logo.style.animationDelay = `-${delay}s`;
        // eslint-disable-next-line no-param-reassign
        logo.style.top = `${(70 - itemHeight) / 2}px`;
      });

      if (containerWidth) {
        // eslint-disable-next-line
        const containerWidthCSS = `@keyframes changePosition { 0% { transform: translateX(${containerWidth /
          2}px) scale(0.5); } 100% { transform: translateX(-${containerWidth /
          2}px) scale(0.5); } }`;
        const style = document.createElement('style');
        style.innerHTML = containerWidthCSS;
        document.head.appendChild(style);
      }

      const duration = containerWidth / pixelsPerSecond;

      logos.forEach(logo => {
        const delay = logo.style.animationDelay;

        // eslint-disable-next-line no-param-reassign
        logo.style.animation = `changePosition ${duration}s linear ${delay} infinite`;
      });
    }
  }, [inView]);

  return (
    <section className="social-proof" ref={ref}>
      <div className="lucid-container">
        <div className="lucid-container--flex">{children}</div>
        <div
          className={`social-proof-slider ${
            !inView ? 'social-proof-slider--pause' : ''
          }`}
        >
          {(inView || hasBeenInView.current) &&
            socialLogos.map(({ alt, src, width, height }) => (
              <img
                key={src}
                alt={alt}
                width={width}
                height={height}
                src={src}
              />
            ))}
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
